import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import {
  Cloud,
  Folder,
  Loader2,
  ChevronRight,
  ChevronDown,
  FileText,
  Image,
  File,
  Check,
  Grid3X3,
  List,
} from "lucide-react";
import dropboxService, {
  DropboxFile,
  DropboxListData,
} from "../../services/dropbox.service";
import Loader from "../../widgets/Loader";
import { BsDropbox } from "react-icons/bs";

interface DropboxConnectProps {
  dropboxToken: string | null;
  setDropboxToken: (token: string | null) => void;
  onUploadSuccess?: () => void;
  documentStoreId?: string;
}

interface BreadcrumbItem {
  id: string;
  name: string;
  path: string;
}

const DropboxConnect: React.FC<DropboxConnectProps> = ({
  dropboxToken,
  setDropboxToken,
  onUploadSuccess,
}) => {
  const [dropboxData, setDropboxData] = useState<DropboxListData | null>(null);
  const [isLoadingDropboxFolders, setIsLoadingDropboxFolders] = useState(false);
  const [selectedDropboxFolder, setSelectedDropboxFolder] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [currentFolder, setCurrentFolder] = useState<DropboxFile[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([
    { id: "root", name: "Dropbox", path: "/" },
  ]);
  const [isUploading, setIsUploading] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "tree">("grid");
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set()
  );

  // Fetch Dropbox folders when token is available
  useEffect(() => {
    if (dropboxToken) {
      fetchDropboxFolders();
    }
  }, [dropboxToken]);

  const handleDropboxAuth = async () => {
    window.open(
      `${import.meta.env.VITE_DMS_API}/dms-v2-srv/dropbox-file-storage-srv/auth`,
      "_blank"
    );
  };

  const fetchDropboxFolders = async () => {
    if (!dropboxToken) return;

    setIsLoadingDropboxFolders(true);
    try {
      const response = await dropboxService.listFiles(dropboxToken);
      if (response.success) {
        setDropboxData(response.data);
        setCurrentFolder(response.data.tree);
      } else if (response.code === 500) {
        toast.error("Token expired. Please reconnect to Dropbox");
        localStorage.removeItem("dropbox_token");
        setDropboxToken(null);
        setDropboxData(null);
      }
    } catch (error: any) {
      console.error("Error fetching Dropbox folders:", error);
      toast.error("Failed to fetch Dropbox folders, please reconnect");
      localStorage.removeItem("dropbox_token");
      setDropboxToken(null);
      setDropboxData(null);
      setSelectedDropboxFolder(null);
    } finally {
      setIsLoadingDropboxFolders(false);
    }
  };

  const handleDropboxFolderSelect = (folderId: string, folderName: string) => {
    if (selectedDropboxFolder?.id === folderId) {
      setSelectedDropboxFolder(null);
    } else {
      setSelectedDropboxFolder({ id: folderId, name: folderName });
    }
  };

  const handleFolderNavigate = (folder: DropboxFile) => {
    if (folder.type === "folder" && folder.children) {
      setCurrentFolder(folder.children);
      setBreadcrumbs([
        ...breadcrumbs,
        { id: folder.id, name: folder.name, path: folder.path_display },
      ]);
      setSelectedDropboxFolder(null);
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
    setBreadcrumbs(newBreadcrumbs);

    const targetPath = newBreadcrumbs[index].path;
    let folderToNavigate = findFolderByPath(dropboxData?.tree || [], targetPath);

    if (index === 0) {
      setCurrentFolder(dropboxData?.tree || []);
    } else if (folderToNavigate && folderToNavigate.children) {
      setCurrentFolder(folderToNavigate.children);
    }
    setSelectedDropboxFolder(null);
  };

  const findFolderByPath = (
    items: DropboxFile[],
    targetPath: string
  ): DropboxFile | null => {
    for (const item of items) {
      if (item.path_display === targetPath && item.type === "folder") {
        return item;
      }
      if (item.children) {
        const found = findFolderByPath(item.children, targetPath);
        if (found) return found;
      }
    }
    return null;
  };

  const handleDisconnect = () => {
    localStorage.removeItem("dropbox_token");
    setDropboxToken(null);
    setDropboxData(null);
    setSelectedDropboxFolder(null);
    setCurrentFolder([]);
    setBreadcrumbs([{ id: "root", name: "Dropbox", path: "/" }]);
    setExpandedFolders(new Set());
  };

  const handleUploadFolder = async () => {
    if (!selectedDropboxFolder) {
      toast.error("Please select a folder");
      return;
    }

    setIsUploading(true);
    try {
      const response = await dropboxService.uploadFolder({
        access_token: dropboxToken || "",
        folder_id: selectedDropboxFolder.id,
        folder_name: selectedDropboxFolder.name,
        ai_performance_level: "MEDIUM",
        file_processing_type: "AI_OCR",
        upload_type: "DROPBOX",
      });

      if (response.success) {
        toast.success(
          `Folder "${selectedDropboxFolder.name}" imported successfully!`
        );
        setSelectedDropboxFolder(null);
        if (onUploadSuccess) {
          onUploadSuccess();
        }
      } else {
        toast.error("Failed to import folder");
      }
    } catch (error: any) {
      console.error("Error importing folder:", error);
        // Handle API error response format
        if (error?.response?.data?.error?.error_description) {
          toast.error(error?.response?.data?.error?.error_description);
        } else if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        } else {
        toast.error("Failed to import folder");
      }
    } finally {
      setIsUploading(false);
    }
  };

  const toggleFolder = (folderId: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const getFileIcon = (file: DropboxFile, size: "small" | "large" = "large") => {
    const sizeClass = size === 'large' ? "h-8 w-8" : "h-5 w-5";
    const extension = file.name.split(".").pop()?.toLowerCase();
    
    if (file.type === "folder") {
      return <Folder className={`${sizeClass} text-blue-500`} />;
    }
    if (["png", "jpg", "jpeg", "gif", "svg", "webp"].includes(extension || "")) {
      return <Image className={`${sizeClass} text-green-500`} />;
    } 
    if (["pdf"].includes(extension || "")) {
      return <FileText className={`${sizeClass} text-red-500`} />;
    }
    if (["doc", "docx", "txt"].includes(extension || "")) {
      return <FileText className={`${sizeClass} text-blue-500`} />;
    }
    return <File className={`${sizeClass} text-gray-500`} />;
  };

  const renderTreeView = (item: DropboxFile, level: number = 0) => {
    if (item.type === 'folder') {
      const hasChildren = item.children && item.children.length > 0;
      const isExpanded = expandedFolders.has(item.id);

      return (
        <div key={item.id} className="select-none">
          <div
            className={`flex items-center gap-2 py-1.5 rounded-lg transition-colors ${selectedDropboxFolder?.id === item.id ? "bg-blue-50" : "hover:bg-gray-50"}`}
            style={{ paddingLeft: `${level * 24 + 8}px` }}
          >
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              {hasChildren && (
                <button
                  onClick={(e) => { e.stopPropagation(); toggleFolder(item.id); }}
                  className="p-0.5 hover:bg-gray-200 rounded"
                >
                  {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-600" /> : <ChevronRight className="h-4 w-4 text-gray-600" />}
                </button>
              )}
            </div>

            <label htmlFor={`folder-${item.id}`} className="flex items-center gap-2.5 cursor-pointer flex-1 min-w-0">
              <input
                type="radio"
                id={`folder-${item.id}`}
                name="dropbox-folder-select"
                checked={selectedDropboxFolder?.id === item.id}
                onChange={() => handleDropboxFolderSelect(item.id, item.name)}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <Folder className="h-5 w-5 text-blue-500 flex-shrink-0" />
              <span className="text-sm text-gray-800 font-medium truncate">
                {item.name}
              </span>
            </label>
          </div>

          {hasChildren && isExpanded && (
            <div className="mt-1">
              {item.children.map((child) => renderTreeView(child, level + 1))}
            </div>
          )}
        </div>
      );
    } else { // It's a file
      return (
        <div
          key={item.id}
          className="flex items-center gap-2.5 py-1.5"
          style={{ paddingLeft: `${level * 24 + 8 + 20 + 10}px` }}
        >
          {getFileIcon(item, 'small')}
          <span className="text-sm text-gray-500 truncate">{item.name}</span>
        </div>
      );
    }
  };

  // Don't render if no token
  if (!dropboxToken) {
    return (
      <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200 mt-3">
        <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
          <BsDropbox className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 leading-tight">
            Connect to Dropbox
          </h3>
          <p className="text-xs text-gray-600 leading-tight mt-1">
            Authorize access to import folders from your Dropbox account
          </p>
        </div>
        <button
          onClick={handleDropboxAuth}
          className="flex-shrink-0 flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
        >
          <BsDropbox className="w-4 h-4" />
          Connect
        </button>
      </div>
    );
  }

  return (
    <div className="mb-6 bg-white border-2 border-gray-200  overflow-hidden mt-3">
      <div className="bg-gray-50 border-b border-gray-200">
        {/* Header with Toolbar */}
        <div className="px-4 py-3 flex items-center justify-between gap-3">
          {/* Left: Breadcrumbs */}
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Cloud className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <div className="flex items-center gap-1 text-sm overflow-x-auto">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={crumb.id}>
                  <button
                    onClick={() => handleBreadcrumbClick(index)}
                    className={`px-2 py-1 rounded hover:bg-blue-200 transition-colors whitespace-nowrap ${
                      index === breadcrumbs.length - 1
                        ? "font-semibold text-blue-900"
                        : "text-blue-700"
                    }`}
                  >
                    {crumb.name}
                  </button>
                  {index < breadcrumbs.length - 1 && (
                    <ChevronRight className="w-4 h-4 text-blue-400" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Right: View Toggle + Import Button */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="flex items-center rounded-lg border border-blue-300 bg-white p-0.5">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-1.5 rounded transition-colors ${
                  viewMode === "grid"
                    ? "bg-blue-600 text-white"
                    : "text-blue-600 hover:bg-blue-50"
                }`}
                title="Grid view"
              >
                <Grid3X3 className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setViewMode("tree")}
                className={`p-1.5 rounded transition-colors ${
                  viewMode === "tree"
                    ? "bg-blue-600 text-white"
                    : "text-blue-600 hover:bg-blue-50"
                }`}
                title="Tree view"
              >
                <List className="h-3.5 w-3.5" />
              </button>
            </div>
            <button
              onClick={handleUploadFolder}
              disabled={!selectedDropboxFolder || isUploading}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition-colors shadow-sm flex items-center gap-1.5"
              title={
                selectedDropboxFolder
                  ? "Import selected folder"
                  : "Select a folder to import"
              }
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Importing...</span>
                </>
              ) : (
                <>
                  <Cloud className="w-3.5 h-3.5" />
                  <span>Import</span>
                </>
              )}
            </button>
            
            {/* Disconnect Button */}
            <button
              onClick={handleDisconnect}
              className="text-xs text-red-600 hover:text-red-700 font-medium px-3 py-1.5 rounded-md hover:bg-red-50 transition-colors flex-shrink-0"
            >
              Disconnect
            </button>
          </div>
        </div>

        {!isLoadingDropboxFolders && (
          <div className="px-4 pt-4 pb-2">
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Folder className="w-4 h-4 text-blue-600" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-gray-900 mb-1">
                  Browse Your Dropbox
                </h4>
                <p className="text-xs text-gray-600 leading-relaxed">
                  {viewMode === "grid"
                    ? "Click to select a folder, then click the arrow icon to open it, or use the 'Import' button to import your selection."
                    : "These are the folders and files in your Dropbox. Expand folders using the arrow, select a folder with the radio button, then click 'Import' to proceed."}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Folder Browser Content */}
      <div className="p-4 bg-white">
        {isLoadingDropboxFolders ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader />
            <p className="mt-4 text-sm text-gray-600 font-medium">
              Loading your Dropbox folders...
            </p>
          </div>
        ) : dropboxData ? (
          <div className="max-h-[500px] overflow-y-auto">
            {viewMode === "tree" ? (
              <div className="space-y-1">
                {dropboxData.tree.map((item) => renderTreeView(item))}
              </div>
            ) : (
              <div>
                {currentFolder && currentFolder.length > 0 ? (
                  <div className="space-y-4">
                    {currentFolder.filter((item) => item.type === "folder")
                      .length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                          Folders ({currentFolder.filter((item) => item.type === "folder").length})
                        </h4>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                          {currentFolder
                            .filter((folder) => folder.type === "folder")
                            .map((folder) => (
                              <div
                                key={folder.id}
                                onClick={() => handleDropboxFolderSelect(folder.id, folder.name)}
                                className={`relative group cursor-pointer rounded-lg border-2 p-3 transition-all ${
                                  selectedDropboxFolder?.id === folder.id
                                    ? "border-blue-500 bg-blue-50"
                                    : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
                                }`}
                              >
                                {selectedDropboxFolder?.id === folder.id && (
                                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                                    <Check className="h-3.5 w-3.5 text-white" />
                                  </div>
                                )}
                                {folder.children && folder.children.length > 0 && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleFolderNavigate(folder); }}
                                      className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-sm border border-gray-200 opacity-0 group-hover:opacity-100 transition-all hover:bg-blue-50 hover:border-blue-300"
                                      title="Open folder"
                                    >
                                      <ChevronRight className="h-3.5 w-3.5 text-blue-600" />
                                    </button>
                                )}
                                <div className="flex flex-col items-center gap-2">
                                  {getFileIcon(folder)}
                                  <span className="text-xs text-center text-gray-700 font-medium line-clamp-2 w-full">
                                    {folder.name}
                                  </span>
                                </div>
                                {folder.children && folder.children.length > 0 && (
                                    <div className="absolute bottom-2 left-2 px-1.5 py-0.5 bg-gray-100 rounded text-xs text-gray-600 font-medium">
                                      {folder.children.length} items
                                    </div>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                    {currentFolder.filter((item) => item.type === "file")
                      .length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                          Files ({currentFolder.filter((item) => item.type === "file").length})
                        </h4>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                          {currentFolder
                            .filter((file) => file.type === "file")
                            .map((file) => (
                              <div key={file.id} className="flex flex-col items-center gap-1 p-2 rounded-lg border border-gray-200 bg-gray-50 opacity-70">
                                {getFileIcon(file)}
                                <span className="text-xs text-center text-gray-600 line-clamp-2 w-full">
                                  {file.name}
                                </span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center py-12">
                    <Folder className="w-16 h-16 text-gray-300 mb-4" />
                    <p className="text-gray-500 text-sm">
                      No items found in this folder
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default DropboxConnect;
